import { useState, useEffect, useMemo, useCallback, useRef } from 'preact/hooks';
import { navigate } from '../lib/navigate';
import { apiFetch, apiFetchRaw, guestApi, reactionApi, ApiError } from '../api/client';
import type { Contribution, ReactionSummary, Thread, ThreadPhaseInventory } from '../api/types';
import type { ReactionAddedEvent, ReactionRemovedEvent } from '../hooks/useThreadEvents';
import { ThreadedContributionList } from '../components/ThreadedContributionList';
import { PresenceBar } from '../components/PresenceBar';
import { LiveToast } from '../components/LiveToast';
import { StaleCacheBanner } from '../components/StaleCacheBanner';
import { Spinner } from '../components/Spinner';
import { useThreadEvents } from '../hooks/useThreadEvents';
import { useAuth } from '../hooks/useAuth';
import styles from './ThreadPage.module.css';

interface ThreadPageProps {
  threadId?: string;
  path?: string;
}

/**
 * Best-effort decode of the `userId` claim from a JWT access token. Unverified —
 * used only to recognize the caller's own reactions client-side (the server
 * remains the source of truth and enforces ownership on delete). Returns null
 * for guests (no token) or a malformed token.
 */
function jwtUserId(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { userId?: string };
    return claims.userId ?? null;
  } catch {
    return null;
  }
}

export function ThreadPage({ threadId }: ThreadPageProps) {
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [thread, setThread] = useState<Thread | null>(null);
  const [phaseInventory, setPhaseInventory] = useState<ThreadPhaseInventory | null>(null);
  const [highlightToken, setHighlightToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [reactions, setReactions] = useState<ReactionSummary>({});
  const { guestSession, accessToken } = useAuth();
  const guestSessionId = guestSession?.sessionId ?? null;
  // The authenticated user's id, decoded from the access token, so realtime
  // reconciliation and the un-react lookup can recognize *this* actor's own
  // rows (guests use the session id; there is no separate user id for them).
  const currentUserId = useMemo(() => jwtUserId(accessToken), [accessToken]);

  // Is a socket event from this client's own actor? If so we already applied it
  // optimistically and must not re-apply it. Precise per-actor identity replaces
  // a timing-based self-echo window so concurrent same-emoji reactions from
  // different actors can't be mis-suppressed.
  const isSelfActor = useCallback(
    (userId?: string | null, guestId?: string | null): boolean =>
      (!!currentUserId && userId === currentUserId) ||
      (!!guestSessionId && guestId === guestSessionId),
    [currentUserId, guestSessionId],
  );

  // Mirror the summary to a ref so the toggle/realtime handlers read current
  // state without re-subscribing the socket effect on every reaction.
  const reactionsRef = useRef<ReactionSummary>({});
  reactionsRef.current = reactions;
  // `${contributionId}:${type}` → reaction id, cached from POST responses so the
  // common un-react path skips a lookup. The DELETE endpoint needs the id.
  const reactionIdMap = useRef<Map<string, string>>(new Map());

  const applyReactionDelta = useCallback(
    (contributionId: string, reactionType: string, delta: 1 | -1, toggleMine: boolean) => {
      setReactions((prev) => {
        const entry = prev[contributionId] ?? { counts: {}, mine: [] };
        const counts = { ...entry.counts };
        const next = (counts[reactionType] ?? 0) + delta;
        if (next > 0) counts[reactionType] = next;
        else delete counts[reactionType];
        let mine = entry.mine;
        if (toggleMine) {
          mine = delta > 0
            ? (entry.mine.includes(reactionType) ? entry.mine : [...entry.mine, reactionType])
            : entry.mine.filter((t) => t !== reactionType);
        }
        return { ...prev, [contributionId]: { counts, mine } };
      });
    },
    [],
  );

  // Realtime reconcile: another actor's reaction adjusts the count only (never
  // our `mine`); our own echo is ignored — already applied optimistically.
  const handleReactionAdded = useCallback((e: ReactionAddedEvent) => {
    if (isSelfActor(e.userId, e.guestSessionId)) return;
    applyReactionDelta(e.contributionId, e.reactionType, 1, false);
  }, [applyReactionDelta, isSelfActor]);

  // Re-pull the phase inventory after the server-side phase/prompt state moves
  // under an open tab. Non-fatal exactly like the mount-time fetch: a failure
  // leaves the last good inventory in place rather than dropping the feed back
  // to first-appearance ordering with no headers.
  const refreshPhaseInventory = useCallback(() => {
    if (!threadId) return;
    guestApi.getThreadPhases(threadId)
      .then((inventory) => {
        if (inventory) setPhaseInventory(inventory);
      })
      .catch(() => undefined);
  }, [threadId]);

  const handleReactionRemoved = useCallback((e: ReactionRemovedEvent) => {
    // The remove payload carries the actor id in `userId` (a guest session id
    // for guests), so match it against both of our identities.
    if (isSelfActor(e.userId, e.userId)) return;
    applyReactionDelta(e.contributionId, e.reactionType, -1, false);
  }, [applyReactionDelta, isSelfActor]);

  const {
    liveContributions,
    presence,
    latestAuthor,
    clearLatestAuthor,
    phaseRecapReady,
    clearPhaseRecapReady,
    narrativeReady,
    clearNarrativeReady,
    narrativeEnhanced,
    clearNarrativeEnhanced,
    recapRenderComplete,
    clearRecapRenderComplete,
    recapRenderFailed,
    clearRecapRenderFailed,
    deliverableReady,
    clearDeliverableReady,
    sphereBalance,
    clearSphereBalance,
    adaptivePromptsReady,
    clearAdaptivePromptsReady,
  } = useThreadEvents(threadId, {
    onReactionAdded: handleReactionAdded,
    onReactionRemoved: handleReactionRemoved,
    // Fires on phase_advanced / turn_changed / elder_rotated. A phase advance
    // changes which phases the inventory reports as started and can swap in a
    // freshly-snapshotted prompt set, so the mount-time fetch goes stale on a
    // tab left open — the section headers would keep printing the prompts the
    // story had when the page loaded.
    onTurnContextInvalidated: () => { refreshPhaseInventory(); },
  });

  // Toggle the caller's reaction for a type: add (optimistic) if not already
  // applied, else remove it. Failures revert the optimistic change.
  const handleReact = useCallback(async (contributionId: string, reactionType: string) => {
    if (!threadId) return;
    const entry = reactionsRef.current[contributionId] ?? { counts: {}, mine: [] };
    const isActive = entry.mine.includes(reactionType);
    const idKey = `${contributionId}:${reactionType}`;

    if (!isActive) {
      applyReactionDelta(contributionId, reactionType, 1, true);
      try {
        const reaction = await reactionApi.add(threadId, contributionId, reactionType);
        if (reaction?.id) reactionIdMap.current.set(idKey, reaction.id);
      } catch {
        applyReactionDelta(contributionId, reactionType, -1, true);
      }
      return;
    }

    applyReactionDelta(contributionId, reactionType, -1, true);
    try {
      let reactionId = reactionIdMap.current.get(idKey);
      if (!reactionId) {
        // Cold path (id not cached this session, e.g. after a reload): find the
        // caller's *own* row for this type — matched by our actor identity so we
        // never target another user's reaction (which the backend would 403).
        const listed = await reactionApi.list(threadId, contributionId);
        const mineRow = (listed?.data ?? []).find((r) =>
          r.reactionType === reactionType
          && ((guestSessionId && r.guestSessionId === guestSessionId)
            || (currentUserId && r.userId === currentUserId)));
        reactionId = mineRow?.id;
      }
      if (!reactionId) throw new Error('reaction id not found');
      await reactionApi.remove(threadId, reactionId);
      reactionIdMap.current.delete(idKey);
    } catch (err) {
      // A 404 means the reaction is already gone server-side (another tab/device
      // removed it — the same guest identity suppresses that echo — or the cached
      // id is stale). The optimistic un-react is then the correct end state: drop
      // the dead id and stay un-reacted, rather than roll back to a phantom the
      // user could never remove (every retry would 404 again).
      if (err instanceof ApiError && err.status === 404) {
        reactionIdMap.current.delete(idKey);
      } else {
        applyReactionDelta(contributionId, reactionType, 1, true);
      }
    }
  }, [threadId, guestSessionId, currentUserId, applyReactionDelta]);

  useEffect(() => {
    if (!threadId) return;

    setLoading(true);
    Promise.all([
      // apiFetchRaw preserves the {data, cursor} envelope (apiFetch would unwrap to just the array).
      apiFetchRaw<{ data: Contribution[]; cursor: string | null }>(
        `/threads/${threadId}/contributions`,
      ),
      // Resolves to GET /api/v2/threads/:threadId (guestOrAuth) — works for both guests and JWT.
      // apiFetch unwraps {data: Thread} to Thread.
      apiFetch<Thread>(`/threads/${threadId}`).catch(() => null),
      // Per-contribution reaction counts + the caller's own reactions. Non-fatal.
      reactionApi.summary(threadId).catch(() => ({} as ReactionSummary)),
      // Phase inventory: canonical section order + the prompts each phase asked.
      // Non-fatal — without it the feed keeps first-appearance ordering.
      guestApi.getThreadPhases(threadId).catch(() => null),
    ])
      .then(([contribResult, threadResult, reactionSummary, inventory]) => {
        setContributions(Array.isArray(contribResult?.data) ? contribResult.data : []);
        setNextCursor(contribResult?.cursor ?? null);
        if (threadResult) {
          setThread(threadResult);
        }
        setPhaseInventory(inventory ?? null);
        const summary = reactionSummary ?? {};
        setReactions(summary);
        // Seed the reaction-id cache from the summary's `mineIds` so an un-react
        // after reload can DELETE by id without the fragile (page-limited) list
        // lookup — which could miss the caller's own row on a popular contribution.
        for (const [cid, entry] of Object.entries(summary)) {
          for (const [type, id] of Object.entries(entry.mineIds ?? {})) {
            reactionIdMap.current.set(`${cid}:${type}`, id);
          }
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [threadId]);

  // Adaptive prompts regenerate in the background and land as a socket event.
  // The inventory serves the phase headers their prompt text, so it has to be
  // re-pulled or the headers keep showing the copy the page loaded with.
  useEffect(() => {
    if (!adaptivePromptsReady) return;
    refreshPhaseInventory();
    clearAdaptivePromptsReady();
  }, [adaptivePromptsReady, clearAdaptivePromptsReady, refreshPhaseInventory]);

  // A declined narrative enhancement (aiEnhanced=false) has nothing to announce —
  // clear it so it can't sit as stale state.
  useEffect(() => {
    if (narrativeEnhanced && !narrativeEnhanced.aiEnhanced) {
      clearNarrativeEnhanced();
    }
  }, [narrativeEnhanced, clearNarrativeEnhanced]);

  // A finished reel usually means the story just wrapped — refresh the thread so
  // the completion banner + its link appear without a manual reload.
  useEffect(() => {
    if (!recapRenderComplete || !threadId) return;
    apiFetch<Thread>(`/threads/${threadId}`)
      .then(setThread)
      .catch(() => {});
  }, [recapRenderComplete, threadId]);

  // The token that reaches the reel. The render event carries one, but it's gone
  // by the next load, and a completed story is mostly met cold — so a finished
  // thread asks. Null while nothing has rendered, which is the banner's cue to
  // offer the written recap and stop saying "watch".
  useEffect(() => {
    if (!threadId || !thread?.isCompleted) return;
    let cancelled = false;
    guestApi.getThreadHighlightToken(threadId)
      .then((res) => {
        if (!cancelled) setHighlightToken(res?.shareToken ?? null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [threadId, thread?.isCompleted, recapRenderComplete]);

  const loadMore = async () => {
    if (!nextCursor || !threadId) return;

    try {
      const result = await apiFetchRaw<{ data: Contribution[]; cursor: string | null }>(
        `/threads/${threadId}/contributions?cursor=${nextCursor}`,
      );
      const next = Array.isArray(result?.data) ? result.data : [];
      setContributions((prev: Contribution[]) => [...prev, ...next]);
      setNextCursor(result?.cursor ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    }
  };

  // Merge REST-fetched contributions with live ones (deduped).
  //
  // Live takes are APPENDED, not prepended. The REST feed is ascending
  // (oldest first) and a socket take is by definition the newest thing in the
  // thread, so putting it in front made it sort before takes that predate it:
  // in timeline mode that produced a today-first date separator followed by
  // older dates, and now that phase sections contain riffs it could render a
  // reply above the parent it replies to.
  const allContributions = useMemo(() => {
    const existingIds = new Set(contributions.map((c: Contribution) => c.id));
    const newLive = liveContributions
      .filter((c: Contribution) => !existingIds.has(c.id))
      // The event hook prepends each arrival, so restore arrival chronology
      // before appending to the oldest-first REST feed.
      .reverse();
    return [...contributions, ...newLive];
  }, [contributions, liveContributions]);

  const handleToastDismiss = useCallback(() => clearLatestAuthor(), [clearLatestAuthor]);

  const handleBuildOn = useCallback((_contribution: Contribution) => {
    navigate(`/contribute/${threadId}`);
  }, [threadId]);

  if (loading) {
    return (
      <div class={styles.loadingContainer}>
        <Spinner size={28} />
        <p class={styles.loadingText}>Loading moment...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div class={styles.errorContainer}>
        <p class={styles.errorText}>{error}</p>
      </div>
    );
  }

  return (
    <div class={styles.container}>
      <StaleCacheBanner />
      {latestAuthor && (
        <LiveToast
          message={`New take just dropped`}
          onDismiss={handleToastDismiss}
        />
      )}
      {phaseRecapReady && (
        <LiveToast
          message={`${phaseRecapReady.phaseName} recap is ready!`}
          duration={5000}
          onDismiss={clearPhaseRecapReady}
        />
      )}
      {narrativeReady && (
        <LiveToast
          message={`Story narrative is ready! ${narrativeReady.authorCount} voices, ${narrativeReady.itemCount} moments.`}
          duration={5000}
          onDismiss={clearNarrativeReady}
        />
      )}
      {narrativeEnhanced?.aiEnhanced && (
        <LiveToast
          message="Your story just got an AI polish."
          duration={5000}
          onDismiss={clearNarrativeEnhanced}
        />
      )}
      {recapRenderComplete && (
        <LiveToast
          message="Your Highlight is ready to watch."
          duration={5000}
          onDismiss={clearRecapRenderComplete}
        />
      )}
      {recapRenderFailed && (
        <LiveToast
          message="We couldn't finish your Highlight. We'll keep trying."
          duration={5000}
          onDismiss={clearRecapRenderFailed}
        />
      )}
      {deliverableReady && (
        <LiveToast
          message={
            deliverableReady.status === 'completed'
              ? 'Your finished moment is ready.'
              : "We couldn't finish your moment."
          }
          duration={5000}
          onDismiss={clearDeliverableReady}
        />
      )}
      {/* Advisory Plot Armor gap nudge — an empty sphere is an open invitation,
          never a block. Backend nudgeMessage arrives in OurTake voice. */}
      {sphereBalance && (
        <LiveToast
          message={sphereBalance.nudgeMessage}
          duration={7000}
          onDismiss={clearSphereBalance}
        />
      )}

      {/* The reel when there's a rendered one to play, the written recap when
          there isn't — and the label says which. "Watch" pointing at prose was
          the same broken promise the completion toast used to make. */}
      {thread?.isCompleted && (
        <div class={styles.completionBanner}>
          <span class={styles.completionIcon}>&#10003;</span>
          <span>This story is complete</span>
          {highlightToken ? (
            <a href={`/r/${encodeURIComponent(highlightToken)}`} class={styles.completionRecapButton}>
              Watch the Highlight
            </a>
          ) : (
            thread.spaceId && (
              <a
                href={`/spaces/${thread.spaceId}/threads/${threadId}/phase-recap/cumulative`}
                class={styles.completionRecapButton}
              >
                Read the Recap
              </a>
            )
          )}
        </div>
      )}

      <div class={styles.header}>
        <h2 class={styles.title}>Moment</h2>
        <div class={styles.headerActions}>
          <button
            onClick={() => navigate(`/contribute/${threadId}`)}
            class={thread?.isCompleted ? styles.contributeButtonSecondary : styles.contributeButton}
          >
            Add Yours
          </button>
          {guestSession && (
            <button
              onClick={() => navigate('/subscription')}
              class={styles.upgradeButton}
            >
              Upgrade
            </button>
          )}
        </div>
      </div>

      <PresenceBar entries={presence} />

      {allContributions.length > 0 && (
        <ThreadedContributionList
          contributions={allContributions}
          onBuildOn={handleBuildOn}
          threadId={threadId}
          reactionsByContribution={reactions}
          onReact={handleReact}
          phaseInventory={phaseInventory}
        />
      )}

      {nextCursor && (
        <button onClick={loadMore} class={styles.loadMoreButton}>
          Load More
        </button>
      )}
    </div>
  );
}
